import { Head, Link } from "@inertiajs/react"
import { useState } from "react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/* FAQ data — restructured around agent objections & business value    */
/* ------------------------------------------------------------------ */

type FaqItem = { question: string; answer: string }

type FaqCategory = {
  title: string
  items: FaqItem[]
}

const FAQ_CATEGORIES: FaqCategory[] = [
  {
    title: "Will this actually help my business?",
    items: [
      {
        question: "Will video marketing really help me win more instructions?",
        answer:
          "Yes. 73% of vendors say they'd be more likely to choose an agent who offers video marketing. When you walk into a market appraisal with a polished video of a similar property, you're showing - not telling - the vendor what their marketing will look like. That's what wins the instruction.",
      },
      {
        question: "Do properties with video actually sell faster?",
        answer:
          "Properties marketed with video receive up to 5x more engagement on social media than static photos. More engagement means more viewings. More viewings means faster sales. Video doesn't replace the fundamentals of good agency work - it amplifies them.",
      },
      {
        question: "I'm a small independent agent. Is this really for me?",
        answer:
          "Especially for you. QuiteRight levels the playing field. The big corporates have in-house marketing teams and video budgets. You have QuiteRight. The result looks the same to the vendor - professional, branded video for every listing. The vendor can't tell whether it cost you £49 or £5,000.",
      },
      {
        question: "My vendors are older and traditional. Do they care about video?",
        answer:
          "They care about results. Show a vendor a 15-second video of their property in a market appraisal - regardless of their age - and watch their face. The reaction is always the same: 'That's impressive.' They don't need to understand social media to appreciate that their agent takes marketing seriously.",
      },
      {
        question: "What's the return on investment?",
        answer:
          "The Agent plan costs £49/month. One additional instruction per year - even on a modest property - more than covers your annual subscription. Most agents report that the first instruction won through video marketing pays for several years of QuiteRight.",
      },
      {
        question: "I already use a videographer for my best properties. Why switch?",
        answer:
          "Don't switch - complement. Use QuiteRight for the 90% of listings that don't justify a videographer visit. The £500-per-property budget still makes sense for your flagship instructions. But the other 19 listings this month? They deserve video too - and now they can have it for pennies each.",
      },
    ],
  },
  {
    title: "Getting started",
    items: [
      {
        question: "How quickly can I create my first video?",
        answer:
          "Under a minute. Sign up, paste a property listing URL, pick a template, and hit render. Your first video will be ready before your next cup of tea. No training, no onboarding call, no learning curve.",
      },
      {
        question: "Do I need any video editing skills?",
        answer:
          "None whatsoever. QuiteRight handles everything - scripting, voiceover, music, rendering, and publishing. If you can paste a URL and click a button, you can create professional property videos.",
      },
      {
        question: "Do I need to install any software?",
        answer:
          "No. QuiteRight runs entirely in your web browser. Chrome, Safari, Firefox, or Edge - on desktop, laptop, or tablet. Nothing to download, nothing to update.",
      },
      {
        question: "Can I try it before paying?",
        answer:
          "Yes. Every plan comes with a 14-day free trial - no credit card required. Import a listing, generate a script, and see the quality before you commit.",
      },
      {
        question: "Who is QuiteRight designed for?",
        answer:
          "UK property professionals: high-street estate agents, independent agents, letting agencies, property managers, and holiday let owners. If you market properties, QuiteRight is built for you.",
      },
      {
        question: "Can I use it for holiday lets and short-term rentals?",
        answer:
          "Absolutely. Import from Airbnb or Booking.com, or enter details manually. The video creation process works the same way - and the results drive direct bookings that save you platform commissions.",
      },
    ],
  },
  {
    title: "How it works",
    items: [
      {
        question: "Which property portals are supported?",
        answer:
          "Rightmove, Zoopla, OnTheMarket, Airbnb, and Booking.com. Paste any listing URL and we extract everything automatically - address, price, photos, features, bedrooms, bathrooms.",
      },
      {
        question: "What if my listing isn't on a supported portal?",
        answer:
          "Enter the details manually. Upload your own photos, type the address and description, and the video creation works exactly the same way. Many agents use this for off-market listings.",
      },
      {
        question: "How are the video scripts generated?",
        answer:
          "We read the property data - address, description, features, price - and write a professional script tailored to the property. It's structured as scenes, each matched to a photo, with a headline and call to action. You can edit, rewrite, or regenerate with one click.",
      },
      {
        question: "What voiceover options are available?",
        answer:
          "Curated British voices in different styles - warm and reassuring, confident and authoritative, friendly and approachable. Preview each one, pick your favourite, and re-record as many times as you like.",
      },
      {
        question: "How long are the videos?",
        answer:
          "15 to 30 seconds - the optimal length for social media engagement. Short enough to hold attention, long enough to show the property at its best.",
      },
      {
        question: "Can I add my agency branding?",
        answer:
          "Yes. Upload your logo and set your brand colours. Every video carries your agency branding. Vendors and buyers see your brand, not ours.",
      },
      {
        question: "Which social platforms can I publish to?",
        answer:
          "Instagram Reels, TikTok, YouTube Shorts, Facebook, and LinkedIn. Connect your accounts once, then publish to any or all platforms with a single click.",
      },
      {
        question: "Are captions generated automatically?",
        answer:
          "Yes. Platform-specific captions with the right hashtags, tone, and length for each channel. You can review and edit every caption before posting.",
      },
    ],
  },
  {
    title: "Pricing and plans",
    items: [
      {
        question: "Is there a free trial?",
        answer:
          "Yes. Every plan comes with a 14-day free trial - no credit card required. Create your account, upload a listing, and see the result. If it doesn't help you win more business, you haven't lost a thing.",
      },
      {
        question: "What's the difference between the three plans?",
        answer:
          "Same result - professional video with your branding. The difference is who does the work. Agent: you create everything. Managed: we create the videos, you share them. Fully Managed: we create, publish, and run your social campaigns. See our Pricing page for full details.",
      },
      {
        question: "Can I switch plans?",
        answer:
          "Yes, anytime. Most agents start on Agent and move to Managed once they see the results. Upgrades take effect immediately; downgrades apply at the start of your next billing period.",
      },
      {
        question: "Is there a contract?",
        answer:
          "No. Month-to-month billing. Cancel anytime - no exit fees, no questions. Your videos remain yours to keep.",
      },
      {
        question: "What happens if I exceed my video limit?",
        answer:
          "Additional videos are £3 each on Agent and Managed plans. On Fully Managed, there are no limits.",
      },
      {
        question: "Do prices include VAT?",
        answer:
          "Prices are exclusive of VAT unless stated otherwise. VAT at the applicable rate will be added where legally required.",
      },
    ],
  },
  {
    title: "Teams and agencies",
    items: [
      {
        question: "Can I invite my team?",
        answer:
          "Yes. Invite colleagues by email. Each person gets their own login but shares the same account - listings, social connections, brand settings, and subscription.",
      },
      {
        question: "Does it work for multi-branch agencies?",
        answer:
          "Yes. Share brand settings and social connections across branches. Team accounts support owners, admins, and members with role-based access control.",
      },
      {
        question: "Can different team members work on different listings?",
        answer:
          "Yes. All team members can see all listings. Multiple people can work on different properties simultaneously without conflicts.",
      },
      {
        question: "What happens if a team member leaves?",
        answer:
          "Remove them from your account. Their login is revoked immediately, but all listings and videos they created remain on the account.",
      },
    ],
  },
  {
    title: "Quality and templates",
    items: [
      {
        question: "What video templates are available?",
        answer:
          "Four distinctive templates: Classic (the all-rounder), Modern (high contrast for urban/new builds), Prestige (luxury feel with gold accents), and Bold (high energy for investment/FTB properties). Each is designed to match a different property type and audience.",
      },
      {
        question: "What formats are supported?",
        answer:
          "Portrait (9:16) for Reels, TikTok, and Shorts. Landscape (16:9) for YouTube and Facebook. Square (1:1) for feeds and LinkedIn. You can render the same listing in multiple formats.",
      },
      {
        question: "What resolution are the videos?",
        answer:
          "Full HD (1080p). Portrait: 1080 × 1920. Landscape: 1920 × 1080. Square: 1080 × 1080. Standard quality for all major social platforms.",
      },
      {
        question: "Can I download the video?",
        answer:
          "Yes. Every video can be downloaded as an MP4 file. Use it on your website, in email campaigns, in listing presentations, or anywhere else you market properties.",
      },
      {
        question: "Is the background music royalty-free?",
        answer:
          "Yes. All tracks are fully licensed for commercial use, including on social media. No copyright strikes, no content claims.",
      },
      {
        question: "Will QuiteRight branding appear on my videos?",
        answer:
          "On paid plans, your agency branding takes centre stage. On the free tier, a small QuiteRight watermark may appear.",
      },
    ],
  },
  {
    title: "Privacy and security",
    items: [
      {
        question: "Is my data secure?",
        answer:
          "Yes. All data is encrypted in transit (TLS 1.2+) and at rest (AES-256). We use secure password hashing, role-based access controls, and regular security assessments. Infrastructure runs on AWS with enterprise-grade security.",
      },
      {
        question: "Are you GDPR compliant?",
        answer:
          "Yes. We comply with UK GDPR and the Data Protection Act 2018. We have a dedicated Data Protection Officer, clear data processing records, and transparent data practices. See our Privacy Policy for full details.",
      },
      {
        question: "Do you sell my data?",
        answer:
          "Never. We only share data with service providers needed to operate the platform (hosting, payments, email) and with social media platforms when you explicitly publish a video.",
      },
      {
        question: "Can I request deletion of my data?",
        answer:
          "Yes. Contact us at team@quiteright.co.uk or close your account. Some data may be retained where we have a legal obligation (e.g. billing records).",
      },
    ],
  },
  {
    title: "Technical support",
    items: [
      {
        question: "What browsers are supported?",
        answer:
          "Chrome, Safari, Firefox, and Edge. We recommend keeping your browser up to date. Internet Explorer is not supported.",
      },
      {
        question: "Can I use QuiteRight on my phone?",
        answer:
          "The platform is optimised for desktop and laptop browsers, which is where most agents work. You can view listings and previews on a tablet, but we recommend a desktop for the full experience.",
      },
      {
        question: "What if something goes wrong?",
        answer:
          "Email us at team@quiteright.co.uk. We aim to respond within one working day, Monday to Friday, 9am to 6pm GMT. On Managed and Fully Managed plans, you get priority and dedicated support respectively.",
      },
      {
        question: "My listing import seems stuck. What should I do?",
        answer:
          "If an import takes longer than a couple of minutes, try refreshing the page. If the problem persists, the listing URL may be behind a paywall or the portal format may have changed. Enter details manually, or contact support.",
      },
    ],
  },
]

const ALL_FAQS = FAQ_CATEGORIES.flatMap((c) => c.items)

/* ------------------------------------------------------------------ */
/* Accordion component                                                 */
/* ------------------------------------------------------------------ */

function FaqAccordion({ items }: { items: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <div className="divide-y divide-stone">
      {items.map((item, i) => {
        const isOpen = openIndex === i
        return (
          <div key={i}>
            <button
              type="button"
              className="flex w-full items-start justify-between gap-4 py-5 text-left"
              onClick={() => setOpenIndex(isOpen ? null : i)}
              aria-expanded={isOpen}
            >
              <span className="text-sm font-semibold text-charcoal leading-relaxed">
                {item.question}
              </span>
              <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200" style={{ transform: isOpen ? "rotate(45deg)" : "rotate(0deg)" }}>
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} className="size-3">
                  <path d="M8 3v10M3 8h10" />
                </svg>
              </span>
            </button>
            <div
              className="overflow-hidden transition-all duration-200"
              style={{ maxHeight: isOpen ? "500px" : "0px", opacity: isOpen ? 1 : 0 }}
            >
              <p className="pb-5 text-sm leading-relaxed text-slate">
                {item.answer}
              </p>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function Faq({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        {/* Structured Data - FAQPage */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: ALL_FAQS.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer,
            },
          })),
        })}</script>
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">FAQ</p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Everything agents ask us<span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Real questions from real agents - about whether it works, how it works,
            and whether it&rsquo;s worth it. Can&rsquo;t find your answer?{" "}
            <Link href="/contact" className="text-red underline underline-offset-2 hover:text-deep-red">Get in touch</Link>.
          </p>
        </div>
      </section>

      {/* FAQ sections */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl space-y-16">
          {FAQ_CATEGORIES.map((category) => (
            <ScrollReveal key={category.title}>
              <h2 className="font-display text-2xl font-bold text-charcoal">
                {category.title}<span className="text-red">.</span>
              </h2>
              <div className="mt-6">
                <FaqAccordion items={category.items} />
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* Useful resources — outgoing authority links */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Useful resources<span className="text-red">.</span>
          </h2>
          <p className="mt-3 text-sm text-slate">
            External resources for UK estate agents looking to learn more about video marketing,
            property technology, and industry regulations.
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {[
              { name: "Propertymark", href: "https://www.propertymark.co.uk", description: "Professional body for property agents — qualifications, standards, and industry news." },
              { name: "The Property Ombudsman", href: "https://www.tpos.co.uk", description: "Independent redress scheme for property consumers in England, Wales, and Scotland." },
              { name: "Rightmove", href: "https://www.rightmove.co.uk", description: "The UK's largest property portal — search for sale and to-let properties." },
              { name: "Zoopla", href: "https://www.zoopla.co.uk", description: "Property search, instant valuations, and market data for the UK property market." },
              { name: "OnTheMarket", href: "https://www.onthemarket.com", description: "Agent-owned property portal — residential sales and lettings nationwide." },
              { name: "Information Commissioner's Office", href: "https://ico.org.uk", description: "The UK's data protection regulator — guidance on GDPR and data rights." },
              { name: "GOV.UK — Stamp Duty", href: "https://www.gov.uk/stamp-duty-land-tax", description: "Official government guidance on Stamp Duty Land Tax rates and thresholds." },
              { name: "Land Registry", href: "https://www.gov.uk/government/organisations/land-registry", description: "HM Land Registry — search property ownership and price paid data." },
            ].map((resource) => (
              <a
                key={resource.name}
                href={resource.href}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg border border-stone p-4 transition-colors hover:border-red/30 hover:bg-cream"
                title={resource.name}
              >
                <p className="text-sm font-semibold text-charcoal">{resource.name} &rarr;</p>
                <p className="mt-1 text-xs text-warm-grey">{resource.description}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Still have questions? */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl">
            Still not sure<span className="text-red">?</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-warm-grey">
            Book a demo and we&rsquo;ll show you exactly how it works with your listings.
            No sales pitch. Just a 15-minute walkthrough.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Book a 15-minute walkthrough
            </Link>
            <Link
              href="/contact"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-6 py-3 text-base font-medium text-cream transition-colors hover:bg-slate sm:w-auto"
            >
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
